import { useEffect, useMemo, useState } from 'react';
import {
  MdBolt,
  MdGroup,
  MdInfoOutline,
  MdEmail,
  MdNotifications,
  MdSend,
  MdSettings,
  MdTune,
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import PageHeader from '../../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  matchTextFilter,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import { useAuth } from '../../../context/AuthContext';
import { usePanelToast } from '../../../context/PanelToastContext';
import { sendNotificationTestEmail } from '../../../services/notifications/sendTestEmailApi';
import {
  getGymSenderEmail,
  ensureGymAdminSenderEmail,
  loadAdminSettings,
  saveAdminSettings,
} from '../settings/settingsData';
import {
  defaultEmailConnection,
  defaultGlobalConfig,
  defaultWhatsAppConnection,
  emailSourceOptions as baseEmailSourceOptions,
  memberReminders as initialMemberReminders,
  notificationHistory as initialHistory,
} from './notificationsData';
import '../../owner/ownerPages.css';
import '../../owner/notifications/notifications.css';
import '../../owner/subscriptions/subscriptions.css';
import '../memberships/memberships.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './notifications.css';

const statusBadge = {
  sent: 'owner-badge-success',
  pending: 'owner-badge-info',
  failed: 'owner-badge-warning',
};

function BigToggle({ checked, onChange, ariaLabel }) {
  return (
    <label className="rec-notif-big-toggle" aria-label={ariaLabel}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="rec-notif-big-toggle-slider" />
    </label>
  );
}

function ReminderRow({ item, onUpdate, onToggleExpand, expanded }) {
  const update = (patch) => onUpdate(item.id, patch);

  return (
    <div className="rec-notif-reminder-row">
      <div className="rec-notif-reminder-main">
        <BigToggle
          checked={item.enabled}
          onChange={(enabled) => update({ enabled })}
          ariaLabel={`Toggle ${item.label}`}
        />
        <div className="rec-notif-reminder-info">
          <div className="rec-notif-reminder-title">{item.label}</div>
          <div className="rec-notif-reminder-slug">{item.id}</div>
        </div>
        <div className="rec-notif-reminder-actions">
          <label className="rec-notif-channel-check">
            <input
              type="checkbox"
              checked={item.email}
              onChange={(e) => update({ email: e.target.checked })}
            />
            Email
          </label>
          {item.whatsapp !== undefined && (
            <label className="rec-notif-channel-check">
              <input
                type="checkbox"
                checked={item.whatsapp}
                onChange={(e) => update({ whatsapp: e.target.checked })}
              />
              WhatsApp
            </label>
          )}
          <button
            type="button"
            className={`rec-notif-configure-btn${expanded ? ' active' : ''}`}
            onClick={() => onToggleExpand(item.id)}
          >
            <MdTune />
            Configure
          </button>
        </div>
      </div>

      {expanded && (
        <div className="rec-notif-config-panel">
          <div className="rec-notif-config-grid">
            {item.hasDaysBefore && (
              <div>
                <label htmlFor={`${item.id}-days`}>Days Before</label>
                <input
                  id={`${item.id}-days`}
                  type="number"
                  min="0"
                  className="owner-form-input"
                  value={item.daysBefore}
                  onChange={(e) => update({ daysBefore: Number(e.target.value) })}
                />
              </div>
            )}
            <div>
              <label htmlFor={`${item.id}-time`}>Send Time</label>
              <input
                id={`${item.id}-time`}
                type="time"
                className="owner-form-input"
                value={item.sendTime}
                onChange={(e) => update({ sendTime: e.target.value })}
              />
            </div>
            <div>
              <label>Channels</label>
              <label className="rec-notif-channel-check" style={{ marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={item.inAppLog}
                  onChange={(e) => update({ inAppLog: e.target.checked })}
                />
                In-App Log
              </label>
            </div>
            <div className="rec-notif-template-field">
              <label htmlFor={`${item.id}-template`}>Message Template</label>
              <textarea
                id={`${item.id}-template`}
                className="owner-form-input"
                value={item.template}
                onChange={(e) => update({ template: e.target.value })}
                rows={3}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const toast = usePanelToast();
  const { account } = useAuth();
  const gymId = account?.gym_id || '';
  const adminEmail = account?.email || '';

  const [activeTab, setActiveTab] = useState('history');
  const [history, setHistory] = useState(initialHistory);
  const [globalConfig, setGlobalConfig] = useState(defaultGlobalConfig);
  const [memberReminders, setMemberReminders] = useState(initialMemberReminders);
  const [expandedId, setExpandedId] = useState('');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [connectForm, setConnectForm] = useState(defaultEmailConnection);
  const [whatsappForm, setWhatsappForm] = useState(defaultWhatsAppConnection);
  const [connectError, setConnectError] = useState('');
  const [whatsappError, setWhatsappError] = useState('');
  const [connectSaving, setConnectSaving] = useState(false);
  const [whatsappSaving, setWhatsappSaving] = useState(false);
  const [autoTestSending, setAutoTestSending] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);
  const [senderEmail, setSenderEmail] = useState('');

  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    recipient: '',
    type: '',
    channel: '',
    status: '',
    sentAt: '',
  });

  const channelLabel = (channel) => channel.charAt(0).toUpperCase() + channel.slice(1);
  const statusLabel = (status) => status.charAt(0).toUpperCase() + status.slice(1);

  // First-time (or missing) sender → lock to this gym admin's signup email
  useEffect(() => {
    if (!gymId && !adminEmail) return;
    const settings = loadAdminSettings(gymId);
    const ensured = ensureGymAdminSenderEmail(
      adminEmail || getGymSenderEmail(gymId),
      settings.gym?.name || '',
      gymId,
    );
    const from = ensured.email?.fromEmail || getGymSenderEmail(gymId) || adminEmail;
    setSenderEmail(from);

    const smtp = {
      ...defaultEmailConnection,
      ...ensured.email,
      fromEmail: from,
      username: ensured.email?.username || from,
      fromName: ensured.email?.fromName || ensured.gym?.name || 'Gym Notifications',
    };

    setGlobalConfig((prev) => ({
      ...prev,
      emailSource: from || prev.emailSource,
      smtp: {
        ...defaultEmailConnection,
        ...prev.smtp,
        ...smtp,
        // Prefer saved SMTP host/password if already connected
        host: prev.smtp?.host || smtp.host || '',
        port: prev.smtp?.port || smtp.port || '587',
        encryption: prev.smtp?.encryption || smtp.encryption || 'tls',
        password: prev.smtp?.password || smtp.password || '',
        fromEmail: from,
        username: prev.smtp?.username || smtp.username || from,
        fromName: prev.smtp?.fromName || smtp.fromName,
      },
    }));
  }, [gymId, adminEmail]);

  const emailSourceOptions = useMemo(() => {
    const opts = [...baseEmailSourceOptions];
    if (senderEmail && !opts.some((o) => o.value === senderEmail)) {
      opts.push({ value: senderEmail, label: `${senderEmail} (gym admin)` });
    }
    return opts;
  }, [senderEmail]);

  const filteredHistory = useMemo(() => {
    return history.filter((item) => (
      matchTextFilter(item.member, filters.recipient)
      && matchTextFilter(item.typeLabel, filters.type)
      && matchTextFilter(channelLabel(item.channel), filters.channel)
      && matchTextFilter(statusLabel(item.status), filters.status)
      && matchTextFilter(item.sentAt, filters.sentAt)
    ));
  }, [history, filters]);

  const updateGlobal = (patch) => setGlobalConfig((prev) => ({ ...prev, ...patch }));

  const updateMemberReminder = (id, patch) => {
    setMemberReminders((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleOpenConnectModal = () => {
    const from = senderEmail || getGymSenderEmail(gymId) || adminEmail;
    const settings = loadAdminSettings(gymId);
    setConnectForm({
      ...defaultEmailConnection,
      ...globalConfig.smtp,
      host: 'smtp.resend.com',
      port: '587',
      encryption: 'tls',
      username: 'resend',
      fromEmail: from,
      fromName: globalConfig.smtp?.fromName || settings.gym?.name || 'Gym Notifications',
      password: '',
    });
    setConnectError('');
    setShowConnectModal(true);
  };

  const handleConnectFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === 'fromEmail') return;
    setConnectForm((prev) => ({ ...prev, [name]: value }));
    setConnectError('');
  };

  const handleConnectEmail = (e) => {
    e.preventDefault();

    const from = senderEmail || getGymSenderEmail(gymId) || adminEmail;
    const apiKey = String(connectForm.password || '')
      .replace(/\s+/g, '')
      .replace(/[\u200B-\u200D\uFEFF]/g, '');

    if (!apiKey || !from) {
      setConnectError('Enter your Resend API key. From email is set from your gym admin account.');
      return;
    }
    if (!apiKey.startsWith('re_')) {
      setConnectError('Resend API key must start with re_');
      return;
    }

    setConnectSaving(true);
    window.setTimeout(() => {
      const smtp = {
        host: 'smtp.resend.com',
        port: '587',
        encryption: 'tls',
        username: 'resend',
        password: apiKey,
        fromName: connectForm.fromName.trim() || 'Gym Notifications',
        fromEmail: from,
      };

      setGlobalConfig((prev) => ({
        ...prev,
        emailConnected: true,
        emailEnabled: true,
        emailSource: from,
        smtp,
      }));

      const settings = loadAdminSettings(gymId);
      saveAdminSettings({
        ...settings,
        adminEmail: from,
        email: {
          ...settings.email,
          ...smtp,
          enabled: true,
        },
      }, gymId);

      setConnectSaving(false);
      setShowConnectModal(false);
      setConnectError('');
      toast.showSaved('Resend email settings');
    }, 600);
  };

  const handleDisconnectEmail = () => {
    setGlobalConfig((prev) => ({
      ...prev,
      emailConnected: false,
      emailEnabled: false,
      emailSource: '',
      smtp: { ...defaultEmailConnection },
    }));
    const settings = loadAdminSettings(gymId);
    saveAdminSettings({
      ...settings,
      email: {
        ...settings.email,
        ...defaultEmailConnection,
        enabled: false,
      },
    }, gymId);
    toast.showSaved('Email disconnected');
  };

  const handleSendTestEmail = async () => {
    if (!globalConfig.emailConnected || !globalConfig.smtp?.password) {
      toast.showError('Connect Resend first, then send a test.');
      return;
    }

    const to = String(
      globalConfig.smtp?.fromEmail
      || senderEmail
      || adminEmail
      || '',
    ).trim();

    if (!to) {
      toast.showError('No recipient found. Connect Email with a From address first.');
      return;
    }

    try {
      setAutoTestSending(true);
      await sendNotificationTestEmail({
        smtp: {
          ...globalConfig.smtp,
          host: 'smtp.resend.com',
          port: '587',
          encryption: 'tls',
          username: 'resend',
        },
        to,
        subject: 'Gym CRM — admin test email',
        note: 'Automatic connection check from Admin → Notifications.',
      });
      toast.showSaved(`Test email sent to ${to} — check Inbox / Spam`);
    } catch (err) {
      toast.showError(err.message || 'Test email failed');
    } finally {
      setAutoTestSending(false);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setWhatsappForm(globalConfig.whatsapp || defaultWhatsAppConnection);
    setWhatsappError('');
    setShowWhatsAppModal(true);
  };

  const handleWhatsAppFieldChange = (e) => {
    const { name, value } = e.target;
    setWhatsappForm((prev) => ({ ...prev, [name]: value }));
    setWhatsappError('');
  };

  const handleConnectWhatsApp = (e) => {
    e.preventDefault();

    if (!whatsappForm.phoneNumber.trim() || !whatsappForm.apiKey.trim()) {
      setWhatsappError('Please fill in business phone number and API key.');
      return;
    }

    setWhatsappSaving(true);
    window.setTimeout(() => {
      const whatsapp = {
        phoneNumber: whatsappForm.phoneNumber.trim(),
        apiKey: whatsappForm.apiKey,
        businessName: whatsappForm.businessName.trim() || 'FitZone Gym',
      };

      setGlobalConfig((prev) => ({
        ...prev,
        whatsappConnected: true,
        whatsappEnabled: true,
        whatsappPhone: whatsapp.phoneNumber,
        whatsapp,
      }));
      setWhatsappSaving(false);
      setShowWhatsAppModal(false);
      setWhatsappError('');
    }, 600);
  };

  const handleDisconnectWhatsApp = () => {
    setGlobalConfig((prev) => ({
      ...prev,
      whatsappConnected: false,
      whatsappEnabled: false,
      whatsappPhone: '',
      whatsapp: { ...defaultWhatsAppConnection },
    }));
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? '' : id));
  };

  return (
    <div>
      <PageHeader
        title={activeTab === 'history' ? 'Notifications' : 'Reminder Settings'}
        subtitle={
          activeTab === 'history'
            ? 'Reminder history, delivery status & manual dispatch.'
            : 'Member reminders, email & WhatsApp automation.'
        }
        variant="banner"
      >
        <div className="rec-notif-header-actions">
          {globalConfig.emailConnected ? (
            <>
              <button
                type="button"
                className="owner-btn owner-btn-primary"
                onClick={handleSendTestEmail}
                disabled={autoTestSending}
              >
                <MdBolt style={{ marginRight: 6 }} />
                {autoTestSending ? 'Sending test…' : 'Test Email'}
              </button>
              <span className="owner-badge owner-badge-success">
                Resend connected · {globalConfig.smtp?.fromEmail || globalConfig.emailSource}
              </span>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={handleOpenConnectModal}>
                Manage Resend
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={handleDisconnectEmail}>
                Disconnect Resend
              </button>
            </>
          ) : (
            <button type="button" className="owner-btn owner-btn-primary" onClick={handleOpenConnectModal}>
              <MdEmail style={{ marginRight: 8 }} />
              Connect Resend
            </button>
          )}

          {globalConfig.whatsappConnected ? (
            <>
              <span className="owner-badge owner-badge-success">
                WhatsApp connected · {globalConfig.whatsappPhone}
              </span>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={handleOpenWhatsAppModal}>
                Manage WhatsApp
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={handleDisconnectWhatsApp}>
                Disconnect WhatsApp
              </button>
            </>
          ) : (
            <button type="button" className="owner-btn owner-btn-primary" onClick={handleOpenWhatsAppModal}>
              <FaWhatsapp style={{ marginRight: 8 }} />
              Connect WhatsApp
            </button>
          )}
        </div>
      </PageHeader>

      <div className="rec-notif-tabs">
        <button
          type="button"
          className={`rec-notif-tab${activeTab === 'history' ? ' active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <MdNotifications style={{ marginRight: 6, verticalAlign: -3 }} />
          Notifications
        </button>
        <button
          type="button"
          className={`rec-notif-tab${activeTab === 'settings' ? ' active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <MdSettings style={{ marginRight: 6, verticalAlign: -3 }} />
          Reminder Settings
        </button>
      </div>

      {activeTab === 'history' ? (
        <>
          <div className="owner-card rec-panel-card rec-member-list-card rec-notif-table-card">
            <div className="owner-table-wrap">
              <table className="owner-table rec-member-table rec-notif-table">
                <thead>
                  <tr>
                    <ColumnFilterHeader
                      label="Recipient"
                      filterKey="recipient"
                      openKey={openFilterKey}
                      onToggle={toggleFilter}
                      filters={filters}
                      onFilterChange={updateFilter}
                  placeholder="Search recipient..."
                    />
                    <ColumnFilterHeader
                      label="Type"
                      filterKey="type"
                      openKey={openFilterKey}
                      onToggle={toggleFilter}
                      filters={filters}
                      onFilterChange={updateFilter}
                  placeholder="Search type..."
                    />
                    <ColumnFilterHeader
                      label="Channel"
                      filterKey="channel"
                      openKey={openFilterKey}
                      onToggle={toggleFilter}
                      filters={filters}
                      onFilterChange={updateFilter}
                  placeholder="Search channel..."
                    />
                    <ColumnFilterHeader
                      label="Status"
                      filterKey="status"
                      openKey={openFilterKey}
                      onToggle={toggleFilter}
                      filters={filters}
                      onFilterChange={updateFilter}
                  placeholder="Search status..."
                    />
                    <ColumnFilterHeader
                      label="Sent At"
                      filterKey="sentAt"
                      openKey={openFilterKey}
                      onToggle={toggleFilter}
                      filters={filters}
                      onFilterChange={updateFilter}
                  placeholder="Search date..."
                    />
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.length > 0 ? (
                    filteredHistory.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div>{item.member}</div>
                          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{item.recipient}</div>
                        </td>
                        <td>{item.typeLabel}</td>
                        <td style={{ textTransform: 'capitalize' }}>{item.channel}</td>
                        <td>
                          <span className={`owner-badge ${statusBadge[item.status] || 'owner-badge-info'}`}>
                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                          </span>
                        </td>
                        <td>{item.sentAt}</td>
                        <td>
                          <button
                            type="button"
                            className="owner-btn owner-btn-secondary owner-btn-sm"
                            onClick={() => setViewTarget(item)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="owner-placeholder-text">No notifications match your filters.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="owner-card rec-notif-section-card">
            <div className="rec-notif-global-config">
              <h3 className="rec-notif-config-heading">
                <MdSettings className="rec-notif-config-heading-icon" />
                Global Config
              </h3>

              <div className="rec-notif-toggle-row">
                <div>
                  <div className="rec-notif-toggle-label">Enable Email Delivery</div>
                  <div className="rec-notif-toggle-desc">Route reminders via email</div>
                </div>
                <BigToggle
                  checked={globalConfig.emailEnabled}
                  onChange={(emailEnabled) => updateGlobal({ emailEnabled })}
                  ariaLabel="Enable email delivery"
                />
              </div>

              <div className="rec-notif-toggle-row">
                <div>
                  <div className="rec-notif-toggle-label">Enable WhatsApp Delivery</div>
                  <div className="rec-notif-toggle-desc">Route reminders via WhatsApp</div>
                </div>
                <BigToggle
                  checked={globalConfig.whatsappEnabled}
                  onChange={(whatsappEnabled) => updateGlobal({ whatsappEnabled })}
                  ariaLabel="Enable WhatsApp delivery"
                />
              </div>

              <div className="owner-form-group owner-mt-20">
                <label className="owner-form-label" htmlFor="email-source">Sender Email Address</label>
                <select
                  id="email-source"
                  className="owner-form-select"
                  value={globalConfig.emailSource}
                  onChange={(e) => updateGlobal({ emailSource: e.target.value })}
                >
                  {emailSourceOptions.map((opt) => (
                    <option key={opt.value || 'auto'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="rec-notif-field-hint">
                  Leave on Auto to always use the currently-connected sender address.
                </div>
              </div>

              <div className="owner-grid-2 owner-mt-20">
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="default-send-time">Default Send Time</label>
                  <input
                    id="default-send-time"
                    type="time"
                    className="owner-form-input"
                    value={globalConfig.defaultSendTime}
                    onChange={(e) => updateGlobal({ defaultSendTime: e.target.value })}
                  />
                  <div className="rec-notif-field-hint">Cron dispatches at configured time for each rule.</div>
                </div>
              </div>

              <div className="rec-notif-toggle-row owner-mt-20">
                <div>
                  <div className="rec-notif-toggle-label">
                    <MdSend style={{ marginRight: 4, verticalAlign: -3 }} />
                    Subscription Invoice Copy
                  </div>
                  <div className="rec-notif-toggle-desc">
                    <MdInfoOutline style={{ marginRight: 4, verticalAlign: -2 }} />
                    Send a copy of subscription invoices to the connected email address.
                  </div>
                </div>
                <BigToggle
                  checked={globalConfig.sendInvoiceCopyToAdmin}
                  onChange={(sendInvoiceCopyToAdmin) => updateGlobal({ sendInvoiceCopyToAdmin })}
                  ariaLabel="Send invoice copy to admin"
                />
              </div>

              <div className="rec-notif-toggle-row">
                <div>
                  <div className="rec-notif-toggle-label">
                    <MdBolt style={{ marginRight: 4, verticalAlign: -3 }} />
                    Auto-send on Save
                  </div>
                  <div className="rec-notif-toggle-desc">
                    When ON, the post-save modal is skipped and the invoice fires immediately. When OFF, staff still get the modal to review and click Send via Email manually.
                  </div>
                </div>
                <BigToggle
                  checked={globalConfig.autoSendOnSave}
                  onChange={(autoSendOnSave) => updateGlobal({ autoSendOnSave })}
                  ariaLabel="Auto-send on save"
                />
              </div>
            </div>
          </div>

          <div className="rec-notif-section-card">
            <h3 className="rec-notif-section-title">
              <MdGroup className="rec-notif-section-icon" />
              Member Reminders
            </h3>
            <p className="rec-notif-section-desc">
              Automatic reminders sent to members — subscription expiry, payments, birthdays, attendance and more.
            </p>
            <div className="rec-notif-reminder-list">
              {memberReminders.map((item) => (
                <ReminderRow
                  key={item.id}
                  item={item}
                  expanded={expandedId === item.id}
                  onToggleExpand={toggleExpand}
                  onUpdate={updateMemberReminder}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {viewTarget && (
        <div className="subscription-modal-overlay" onClick={() => setViewTarget(null)}>
          <div
            className="subscription-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="view-email-title"
          >
            <div className="subscription-modal-header">
              <h2 id="view-email-title" className="subscription-modal-title">Email Message</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setViewTarget(null)} aria-label="Close">×</button>
            </div>
            <div className="subscription-form">
              <div className="owner-form-group">
                <label className="owner-form-label">Recipient</label>
                <div className="owner-form-input" style={{ background: 'var(--color-bg-dark)', cursor: 'default' }}>
                  {viewTarget.member} · {viewTarget.recipient}
                </div>
              </div>
              <div className="owner-grid-2">
                <div className="owner-form-group">
                  <label className="owner-form-label">Type</label>
                  <div className="owner-form-input" style={{ background: 'var(--color-bg-dark)', cursor: 'default' }}>
                    {viewTarget.typeLabel}
                  </div>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label">Status</label>
                  <div className="owner-form-input" style={{ background: 'var(--color-bg-dark)', cursor: 'default' }}>
                    {viewTarget.status.charAt(0).toUpperCase() + viewTarget.status.slice(1)}
                  </div>
                </div>
              </div>
              <div className="owner-form-group">
                <label className="owner-form-label">Message</label>
                <textarea
                  className="owner-form-input"
                  rows={5}
                  readOnly
                  value={viewTarget.message}
                  style={{ resize: 'vertical', background: 'var(--color-bg-dark)' }}
                />
              </div>
              <div className="subscription-form-actions">
                <button type="button" className="owner-btn owner-btn-primary" onClick={() => setViewTarget(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConnectModal && (
        <div className="subscription-modal-overlay" onClick={() => setShowConnectModal(false)}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="connect-email-title"
          >
            <div className="subscription-modal-header">
              <h2 id="connect-email-title" className="subscription-modal-title">
                {globalConfig.emailConnected ? 'Manage Resend Connection' : 'Connect Resend'}
              </h2>
              <button type="button" className="subscription-modal-close" onClick={() => setShowConnectModal(false)} aria-label="Close">×</button>
            </div>
            <form className="subscription-form" onSubmit={handleConnectEmail}>
              <div className="owner-grid-2">
                <div className="owner-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="owner-form-label" htmlFor="connect-resend-api-key">Resend API Key</label>
                  <input
                    id="connect-resend-api-key"
                    name="password"
                    type="password"
                    className="owner-form-input"
                    value={connectForm.password}
                    onChange={handleConnectFieldChange}
                  placeholder="re_…"
                    autoComplete="new-password"
                    required
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-from-name">From Name</label>
                  <input
                    id="connect-from-name"
                    name="fromName"
                    className="owner-form-input"
                    value={connectForm.fromName}
                    onChange={handleConnectFieldChange}
                  placeholder="FitZone Gym"
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-from-email">From Email</label>
                  <input
                    id="connect-from-email"
                    name="fromEmail"
                    type="email"
                    className="owner-form-input"
                    value={connectForm.fromEmail || senderEmail}
                    readOnly
                    required
                  />
                </div>
              </div>

              {connectError && <p className="membership-modal-message" style={{ color: 'var(--color-danger)' }}>{connectError}</p>}

              <div className="subscription-form-actions">
                <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setShowConnectModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="owner-btn owner-btn-primary" disabled={connectSaving}>
                  {connectSaving ? 'Connecting...' : globalConfig.emailConnected ? 'Save Changes' : 'Connect Resend'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showWhatsAppModal && (
        <div className="subscription-modal-overlay" onClick={() => setShowWhatsAppModal(false)}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="connect-whatsapp-title"
          >
            <div className="subscription-modal-header">
              <h2 id="connect-whatsapp-title" className="subscription-modal-title">
                {globalConfig.whatsappConnected ? 'Manage WhatsApp Connection' : 'Connect WhatsApp'}
              </h2>
              <button type="button" className="subscription-modal-close" onClick={() => setShowWhatsAppModal(false)} aria-label="Close">×</button>
            </div>
            <form className="subscription-form" onSubmit={handleConnectWhatsApp}>
              <p className="rec-notif-field-hint" style={{ marginBottom: 16 }}>
                Enter your WhatsApp Business API credentials to send member reminders via WhatsApp.
              </p>
              <div className="owner-grid-2">
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-wa-business">Business Name</label>
                  <input
                    id="connect-wa-business"
                    name="businessName"
                    className="owner-form-input"
                    value={whatsappForm.businessName}
                    onChange={handleWhatsAppFieldChange}
                  placeholder="FitZone Gym"
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="connect-wa-phone">Business Phone Number</label>
                  <input
                    id="connect-wa-phone"
                    name="phoneNumber"
                    className="owner-form-input"
                    value={whatsappForm.phoneNumber}
                    onChange={handleWhatsAppFieldChange}
                  placeholder="+92 300 1112233"
                    required
                  />
                </div>
                <div className="owner-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="owner-form-label" htmlFor="connect-wa-api-key">API Key</label>
                  <input
                    id="connect-wa-api-key"
                    name="apiKey"
                    type="password"
                    className="owner-form-input"
                    value={whatsappForm.apiKey}
                    onChange={handleWhatsAppFieldChange}
                  placeholder="Enter WhatsApp API key"
                    required
                  />
                </div>
              </div>

              {whatsappError && <p className="membership-modal-message" style={{ color: 'var(--color-danger)' }}>{whatsappError}</p>}

              <div className="subscription-form-actions">
                <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setShowWhatsAppModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="owner-btn owner-btn-primary" disabled={whatsappSaving}>
                  {whatsappSaving ? 'Connecting...' : globalConfig.whatsappConnected ? 'Save Changes' : 'Connect WhatsApp'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Notifications;
